import * as React from "react"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { toneClass, TONE_TEXT_CLASS } from "@/lib/status"

/*
  StatCard — KPI tile: [icon tile] label ›  /  big value + unit  /  optional progress  /  meta line.

    <StatCard label="Awaiting approval" icon={Stamp} tone="amber" value={16} unit="stages"
              meta="Oldest 798 days" metaTone="red" to="/projects?filter=awaiting" />
    <StatCard label="Unpaid invoices" icon={ReceiptText} prefix="AED" value={formatNumber(428906, { decimals: 0 })} />
    <StatCard label="Invoiced" value="200,282" prefix="AED" progress={46} progressTone="lime" meta="46% of contract" />

  Clickable when given `to` (router link), `href` (plain link) or `onClick` (button) — then shows a ›.
  Props: label, value, prefix (small, before value), unit (small, after value), icon (lucide component),
  tone (icon tile tone: navy default), valueTone, meta (node), metaTone, progress (0–100), progressTone
  ('navy' | 'lime' | 'green' | 'amber' | 'red' | 'blue'), progressLabel (accessible name of the bar;
  defaults to `label` when it is a string), loading, children (extra content under meta), className,
  size ('default' | 'compact' — 12px 14px padding and a 24px value, for rows of small counters).
*/

const PROGRESS_FILL = {
  navy: "bg-[color:var(--brand)]",
  lime: "bg-[color:var(--lime-600)]",
  green: "bg-status-green",
  amber: "bg-status-amber",
  red: "bg-status-red",
  blue: "bg-status-blue",
}

const StatCard = React.forwardRef(({
  label,
  value,
  prefix,
  unit,
  icon: Icon,
  tone = "navy",
  valueTone,
  meta,
  metaTone,
  progress,
  progressTone = "navy",
  progressLabel,
  to,
  href,
  onClick,
  loading = false,
  size = "default",
  className,
  children,
  ...props
}, ref) => {
  const compact = size === "compact"
  const interactive = Boolean(to || href || onClick)
  const pct = progress === undefined || progress === null ? null : Math.max(0, Math.min(100, Number(progress) || 0))

  const body = (
    <>
      <div className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-3">
        {Icon ? (
          <span className={cn("ui-icon-tile h-7 w-7 rounded-lg", toneClass(tone))} aria-hidden="true">
            <Icon className="h-[15px] w-[15px]" />
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {interactive ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-4 transition-transform group-hover:translate-x-0.5 group-hover:text-ink-3" aria-hidden="true" />
        ) : null}
      </div>
      {loading ? (
        <div className="ui-skeleton-shimmer h-7 w-24 rounded-md" role="status">
          <span className="sr-only">Loading</span>
        </div>
      ) : (
        <div
          className={cn(
            "flex min-w-0 items-baseline gap-1.5 font-heading font-black tracking-[-0.03em] text-ink",
            compact ? "text-[24px]" : "text-[28px]",
            // after the size: tailwind-merge drops a leading-* that comes before a text-[size] class
            "leading-[1.05]",
            valueTone && TONE_TEXT_CLASS[valueTone]
          )}
        >
          {prefix ? (
            <small className="text-[13px] font-bold tracking-normal text-ink-3" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
              {prefix}
            </small>
          ) : null}
          <span className="truncate tabular-nums">{value ?? "—"}</span>
          {unit ? (
            <small className="text-[13px] font-semibold tracking-normal text-ink-3" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
              {unit}
            </small>
          ) : null}
        </div>
      )}
      {pct !== null ? (
        <div
          className="h-1.5 overflow-hidden rounded-full bg-[color:var(--line)]"
          role="progressbar"
          aria-label={progressLabel ?? (typeof label === "string" ? label : "Progress")}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className={cn("block h-full rounded-full", PROGRESS_FILL[progressTone] || PROGRESS_FILL.navy)} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {meta ? (
        <div className={cn("truncate text-[12px] text-ink-3", metaTone && cn("font-semibold", TONE_TEXT_CLASS[metaTone]))}>
          {meta}
        </div>
      ) : null}
      {children}
    </>
  )

  const classes = cn(
    "group flex min-w-0 flex-col gap-2 rounded-card border border-line bg-surface text-left text-ink shadow-card",
    compact ? "px-3.5 py-3" : "px-[18px] py-4",
    interactive && "cursor-pointer transition-[border-color,box-shadow] hover:border-[color:var(--brand-soft-border)] hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    className
  )

  if (to) {
    return <Link ref={ref} to={to} className={classes} onClick={onClick} {...props}>{body}</Link>
  }
  if (href) {
    return <a ref={ref} href={href} className={classes} onClick={onClick} {...props}>{body}</a>
  }
  if (onClick) {
    return <button ref={ref} type="button" className={cn(classes, "w-full")} onClick={onClick} {...props}>{body}</button>
  }
  return <div ref={ref} className={classes} {...props}>{body}</div>
})
StatCard.displayName = "StatCard"

export { StatCard }
